import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import vmHero from "../assets/Hero/IOT.webp"; // Replace with your hero image
import vmImage from "../assets/Hero/virtual-machine.jpg"; // Replace with your virtual machines image
import featuresImage from "../assets/Hero/Nairobi.jpg"; // Replace with your features image
import "../assets/css/virtualMachinesPage.css";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// JSON data for headings and paragraphs
const vmData = {
  hero: {
    title: "Virtual Machines Solutions",
    subtitle: "Empowering Your Business with Scalable and Secure Virtualization",
  },
  introduction: {
    heading: "Why Virtual Machines Matter",
    paragraphs: [
      "Virtual machines (VMs) provide a flexible and cost-effective way to run multiple operating systems and applications on a single physical server.",
      "Our virtual machine solutions are designed to optimize performance, enhance security, and provide scalability for your business needs.",
    ],
  },
  features: {
    heading: "Our Virtual Machines Features",
    items: [
      "High-Performance Virtualization",
      "Scalable Resource Allocation",
      "Secure Isolated Environments",
      "Automated Backup & Recovery",
      "Cross-Platform Compatibility",
      "24/7 Monitoring & Support",
    ],
  },
  benefits: {
    heading: "Benefits of Our Solutions",
    items: [
      {
        icon: "fa fa-server",
        text: "Improved resource utilization and cost savings.",
      },
      {
        icon: "fa fa-shield",
        text: "Enhanced security with isolated environments.",
      },
      {
        icon: "fa fa-line-chart",
        text: "Scalable solutions to meet growing demands.",
      },
      {
        icon: "fa fa-check-circle",
        text: "Compliance with industry standards and regulations.",
      },
      {
        icon: "fa fa-headphones",
        text: "Expert support for seamless operations.",
      },
    ],
  },
  cta: {
    heading: "Ready to Transform Your Infrastructure?",
    text: "Contact us today to learn more about our Virtual Machines Solutions and how we can help your business thrive.",
  },
};

export const VirtualMachinesPage = ({ data }) => {
  const vmRef = useRef(null);
  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    const elements = vmRef.current.querySelectorAll(".vm-animate");

    gsap.fromTo(
      elements,
      { y: 100, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.0,
        ease: "power3.out",
        stagger: 0.2,
        scrollTrigger: {
          trigger: vmRef.current,
          start: "top 80%",
          toggleActions: "play none none reset",
        },
      }
    );
  }, []);

  // Function to handle navigation
  const handleContactClick = () => {
    navigate("/contact"); // Navigate to the contact page
  };

  return (
    <div id="vm-page" ref={vmRef}>
      {/* Hero Section */}
      <div
        className="vm-hero-section"
        style={{ backgroundImage: `url(${vmHero})` }}
      >
        <h1 className="vm-animate">{vmData.hero.title}</h1>
        <p className="vm-animate">{vmData.hero.subtitle}</p>
      </div>

      {/* Introduction Section */}
      <div className="container vm-introduction">
        <div className="row">
          <div className="col-md-6">
            <h2 className="vm-animate">{vmData.introduction.heading}</h2>
            {vmData.introduction.paragraphs.map((paragraph, index) => (
              <p key={index} className="vm-animate">
                {paragraph}
              </p>
            ))}
          </div>
          <div className="col-md-6 vm-image-container">
            <img
              src={vmImage}
              className="img-fluid vm-animate"
              alt="Virtual Machines Solutions"
            />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container vm-features">
        <div className="row">
          <div className="col-md-6 vm-image-container">
            <img
              src={vmHero}
              className="img-fluid vm-animate"
              alt="Virtual Machines Features"
              style={{ maxWidth: "100%" }}
            />
          </div>
          <div className="col-md-6">
            <h2 className="vm-animate">{vmData.features.heading}</h2>
            <ul>
              {vmData.features.items.map((item, index) => (
                <li key={index} className="vm-animate">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div
        className="container-fluid vm-benefits"
        style={{ backgroundImage: `url(${featuresImage})` }}
      >
        <div className="overlay">
          <h2 className="vm-animate">{vmData.benefits.heading}</h2>
          <div className="benefits-cards">
            {vmData.benefits.items.map((item, index) => (
              <div key={index} className="benefit-card vm-animate">
                <i className={item.icon}></i>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="container vm-cta">
        <h2 className="vm-animate">{vmData.cta.heading}</h2>
        <p className="vm-animate">{vmData.cta.text}</p>
        <button
          className="btn btn-primary vm-animate"
          onClick={handleContactClick} // Add onClick handler
        >
          Contact Us
        </button>
      </div>
    </div>
  );
};